/**
 * API keys — developer mode surface.
 *
 * Cache an existing key in the session, mint a new key, or revoke
 * existing ones. The raw key is shown ONCE via the RawKeyModal at mint
 * time; after that only prefix + last4 + last-used are recoverable.
 */

import { useEffect, useState } from "react";
import {
  ApiError,
  listApiKeys,
  mintApiKey,
  revokeApiKey,
  type ApiKey,
  type ApiKeyMode,
  type MintedApiKey,
} from "../lib/api";
import { Banner, Btn } from "../components/ui";
import { Ic } from "../components/Icons";
import {
  RawKeyModal,
  clearStoredApiKey,
  getStoredApiKey,
  setStoredApiKey,
} from "../components/RawKeyModal";

const formatRelative = (t: number): string => {
  const delta = Date.now() - t;
  const min = 60_000;
  const hour = 3_600_000;
  const day = 86_400_000;
  if (delta < min) return "just now";
  if (delta < hour) return `${Math.floor(delta / min)}m ago`;
  if (delta < day) return `${Math.floor(delta / hour)}h ago`;
  return `${Math.floor(delta / day)}d ago`;
};

export function ApiKeysScreen() {
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("default");
  const [mode, setMode] = useState<ApiKeyMode>("live");
  const [busy, setBusy] = useState(false);
  const [minted, setMinted] = useState<MintedApiKey | null>(null);
  const [pasteVisible, setPasteVisible] = useState(false);
  const [pasteValue, setPasteValue] = useState("");
  const [cached, setCached] = useState<string | null>(getStoredApiKey());

  const refresh = async () => {
    try {
      const r = await listApiKeys();
      setKeys(r.data);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const handleMint = async () => {
    setBusy(true);
    setError(null);
    try {
      const k = await mintApiKey({
        name: name.trim() || "default",
        mode,
      });
      setMinted(k);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (id: number) => {
    try {
      await revokeApiKey(id);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const handleCacheFromPaste = () => {
    const v = pasteValue.trim();
    if (!v.startsWith("sk_live_") && !v.startsWith("sk_test_")) {
      setError("Pasted value doesn't look like a Shieldz API key.");
      return;
    }
    setStoredApiKey(v);
    setCached(v);
    setPasteVisible(false);
    setPasteValue("");
  };

  if (minted) {
    return (
      <div className="scroll">
        <div className="pad fade-in">
          <RawKeyModal
            mintedKey={minted}
            onDismiss={() => {
              setMinted(null);
              setCached(getStoredApiKey());
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="scroll">
      <div className="pad fade-in">
        {error && (
          <div style={{ marginBottom: 12 }}>
            <Banner tone="fail">{error}</Banner>
          </div>
        )}

        <div className="s-label">Cached key for this session</div>
        <div className="card card-pad">
          {cached ? (
            <>
              <div className="help" style={{ marginBottom: 10 }}>
                Currently using: <code className="mono">{cached.slice(0, 12)}…{cached.slice(-4)}</code>
              </div>
              <Btn
                variant="secondary"
                onClick={() => {
                  clearStoredApiKey();
                  setCached(null);
                }}
              >
                Forget cached key
              </Btn>
            </>
          ) : (
            <>
              <div className="help" style={{ marginBottom: 10 }}>
                Dashboard reads webhooks + deliveries via your API key. Paste
                an existing key for this session or mint a fresh one below.
              </div>
              {pasteVisible ? (
                <div className="field">
                  <input
                    className="input mono"
                    type="text"
                    value={pasteValue}
                    onChange={(e) => setPasteValue(e.target.value)}
                    placeholder="sk_live_…"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <Btn onClick={handleCacheFromPaste} style={{ marginTop: 8 }}>
                    Cache for this tab
                  </Btn>
                </div>
              ) : (
                <Btn variant="secondary" onClick={() => setPasteVisible(true)}>
                  Paste existing key
                </Btn>
              )}
            </>
          )}
        </div>

        <div className="s-label">Mint a new key</div>
        <div className="card card-pad">
          <div className="field">
            <label htmlFor="name">Name (for your reference)</label>
            <input
              id="name"
              className="input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. production-server"
              maxLength={80}
            />
          </div>
          <div className="field" style={{ marginTop: 12 }}>
            <label htmlFor="mode">Mode</label>
            <select
              id="mode"
              className="input"
              value={mode}
              onChange={(e) => setMode(e.target.value as ApiKeyMode)}
            >
              <option value="live">live (real swaps)</option>
              <option value="test">test (simulated webhooks)</option>
            </select>
          </div>
          <Btn
            onClick={() => void handleMint()}
            loading={busy}
            style={{ marginTop: 16 }}
          >
            Mint key
          </Btn>
        </div>

        <div className="s-label">Existing keys</div>
        <div className="card">
          {keys === null ? (
            <div className="card-pad" style={{ color: "var(--ink-3)" }}>
              Loading…
            </div>
          ) : keys.length === 0 ? (
            <div className="empty">
              <h4>No keys yet</h4>
              <p>Mint one above when you're ready to integrate from your server.</p>
            </div>
          ) : (
            keys.map((k) => (
              <div key={k.id} className="li">
                <div className="li-main">
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{k.name}</div>
                  <div className="li-meta">
                    {k.prefix}…{k.last4} · created {formatRelative(k.created_at)}
                    {k.last_used_at
                      ? ` · last used ${formatRelative(k.last_used_at)}`
                      : " · never used"}
                    {k.revoked_at ? " · revoked" : ""}
                  </div>
                </div>
                <div className="li-right">
                  <span
                    className={`pill ${k.mode === "live" ? "warn" : "muted"}`}
                  >
                    <span className="pd" />
                    {k.mode}
                  </span>
                  {!k.revoked_at && (
                    <Btn
                      variant="ghost"
                      onClick={() => void handleRevoke(k.id)}
                      style={{ padding: "4px 10px", color: "var(--danger-ink)" }}
                    >
                      <Ic name="trash" size={14} /> Revoke
                    </Btn>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
